import { useState, useMemo } from "react";
import React from 'react';
import ReactTableWrapper from "../../components/ReactTableWrapper";
import "../../App.css";
import { useCookies } from 'react-cookie';
import { Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { default as ReactSelect } from "react-select";
import { DatePicker } from 'antd';
import Swal from 'sweetalert2';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllCustomerSites } from '../../services/CustomerSiteServices';
import { mapConsigneeListToSiteOptions } from '../../utils/customerSiteOptionsUtils';
import { getAllEquipments } from '../../services/EquipmentServices';
import { getAllGroups } from '../../services/GroupFormServices';
import { getDispatchReport } from "../../services/ReportServices";
import { getEquipmentTypeOptions } from '../Transport/EquipmentUtils';
import SettingsIcon from '../../components/SettingsIcon';
import moment from 'moment';
import { displayError, getApiErrorMessage } from '../../helpers';
import { HandleSelect, ValueContainer, Option } from "../../components/MultiSelectControls";
import DispatchSummaryChart from '../../components/DispatchSummaryChart';

const { RangePicker } = DatePicker;
const EQUIPMENT_REPORT_SETTINGS_KEY = 'equipment_report_settings';
const DEFAULT_EQUIPMENT_GROUP_NAME = 'TRANSIT MIXER';

function formatEquipGroupLabel(name) {
    return String(name || '')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function readSavedSettings() {
    try {
        const raw = localStorage.getItem(EQUIPMENT_REPORT_SETTINGS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function formatTwoDecimals(value) {
    if (value === '' || value === null || value === undefined) {
        return '';
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return '';
    }
    return num.toFixed(2);
}

function mapEquipmentReportRow(dc) {
    return {
        id: dc.id,
        plant_name: dc.plant_name || '',
        plant_alias: dc.plant_alias || dc.plant_name || '',
        production_plant_name: dc.production_plant_name || '',
        delivery_challan_date: dc.delivery_challan_date || '',
        delivery_challan_no: dc.delivery_challan_no || '',
        batch_no: dc.batch_no || '',
        delivery_challan_time: dc.delivery_challan_time || '',
        sales_order_no: dc.sales_order_no || '',
        purchase_order_no: dc.purchase_order_no || '',
        invoice_number: dc.invoice_number || '',
        customer_name: dc.customer_name || '',
        project_name: dc.project_name || '',
        dc_grade: dc.dc_grade || '',
        dc_qty: formatTwoDecimals(dc.dc_qty),
        rate: formatTwoDecimals(dc.rate),
        amount: formatTwoDecimals(dc.amount),
        delivery_mode: (dc.delivery_mode || '').toUpperCase(),
        vehicle_no: dc.vehicle_no || '',
        salesrep_name: dc.salesrep_name || '',
        driver_name: dc.driver_name || '',
        total_km: dc.total_km ?? '',
        net_weight: dc.net_weight ?? '',
        customer_dc_status: dc.customer_dc_status || '',
        pump: dc.pump || '',
        gang: dc.gang || '',
    };
}

const EQUIPMENT_REPORT_COLUMNS = [
    { fieldName: 'vehicle_no', headerName: 'Vehicle No' },
    { fieldName: 'delivery_challan_date', headerName: 'Date' },
    { fieldName: 'delivery_challan_no', headerName: 'DC No.' },
    { fieldName: 'delivery_challan_time', headerName: 'Time' },
    { fieldName: 'customer_name', headerName: 'Customer Name' },
    { fieldName: 'project_name', headerName: 'Site/Project Name' },
    { fieldName: 'dc_grade', headerName: 'Grade' },
    { fieldName: 'dc_qty', headerName: 'Quantity', align: 'right' },
    { fieldName: 'delivery_mode', headerName: 'Mode of Delivery' },
    { fieldName: 'salesrep_name', headerName: 'Marketing' },
    { fieldName: 'driver_name', headerName: 'Driver name' },
    { fieldName: 'total_km', headerName: 'Total Km' },
];

const EQUIPMENT_CONDENSED_COLUMNS = [
    { fieldName: 'vehicle_no', headerName: 'Vehicle No' },
    { fieldName: 'delivery_challan_date', headerName: 'Date' },
    { fieldName: 'customer_name', headerName: 'Customer Name' },
    { fieldName: 'project_name', headerName: 'Site/Project Name' },
    { fieldName: 'dc_grade', headerName: 'Grade' },
    { fieldName: 'delivery_mode', headerName: 'Mode of Delivery' },
    { fieldName: 'dc_qty', headerName: 'Quantity', align: 'right' },
];

const EQUIPMENT_GROUP_FIELD = {
    datewise: 'delivery_challan_date',
    customerwise: 'customer_name',
    vehiclewise: 'vehicle_no',
    driverwise: 'driver_name',
};

const compareText = (left, right) => String(left || '').localeCompare(
    String(right || ''),
    undefined,
    { sensitivity: 'base', numeric: true },
);

const parseEquipDateKey = (value) => {
    const parsed = moment(value, 'DD-MM-YYYY', true);
    return parsed.isValid() ? parsed.valueOf() : 0;
};

function aggregateEquipmentCondensedRows(rows) {
    const groups = new Map();
    const orderedKeys = [];

    rows.forEach((row) => {
        const mode = (row.delivery_mode || '').toUpperCase();
        const key = [
            row.vehicle_no || '',
            row.delivery_challan_date || '',
            row.customer_name || '',
            row.project_name || '',
            row.dc_grade || '',
            mode,
        ].join('|||');

        if (!groups.has(key)) {
            groups.set(key, {
                id: `condensed-${orderedKeys.length}`,
                vehicle_no: row.vehicle_no || '',
                delivery_challan_date: row.delivery_challan_date || '',
                customer_name: row.customer_name || '',
                project_name: row.project_name || '',
                dc_grade: row.dc_grade || '',
                delivery_mode: mode,
                driver_name: row.driver_name || '',
                dc_qty: 0,
            });
            orderedKeys.push(key);
        }

        groups.get(key).dc_qty += Number(row.dc_qty || 0);
    });

    return orderedKeys.map((key) => {
        const row = groups.get(key);
        return {
            ...row,
            dc_qty: formatTwoDecimals(row.dc_qty),
        };
    });
}

function buildEquipmentGroupedRows(rows, reportFormat) {
    const groupField = EQUIPMENT_GROUP_FIELD[reportFormat];
    if (!groupField || !rows.length) {
        return rows;
    }

    const orderedKeys = [];
    const groups = new Map();
    rows.forEach((row) => {
        const key = row[groupField] || 'Unassigned';
        if (!groups.has(key)) {
            groups.set(key, []);
            orderedKeys.push(key);
        }
        groups.get(key).push(row);
    });

    if (reportFormat === 'datewise') {
        orderedKeys.sort((left, right) => parseEquipDateKey(left) - parseEquipDateKey(right));
    } else {
        orderedKeys.sort(compareText);
    }

    const groupedRows = [];
    orderedKeys.forEach((key) => {
        const groupRows = groups.get(key) || [];
        const groupQty = groupRows.reduce((sum, row) => sum + Number(row.dc_qty || 0), 0);
        groupedRows.push({
            id: `group-${groupField}-${key}`,
            is_group_header: true,
            // Group title always in the first column (Vehicle No).
            vehicle_no: key,
            dc_qty: groupQty.toFixed(2),
        });
        groupedRows.push(...groupRows);
    });
    return groupedRows;
}

function buildPeriodDateKeys(fromDate, toDate) {
    const keys = [];
    const cursor = fromDate.clone().startOf('day');
    const end = toDate.clone().startOf('day');
    while (cursor.isSameOrBefore(end, 'day')) {
        keys.push(cursor.format('DD-MM-YYYY'));
        cursor.add(1, 'day');
    }
    return keys;
}

function summaryDateField(dateStr) {
    return `d_${String(dateStr || '').replace(/-/g, '_')}`;
}

function formatSummaryValue(value, summaryMode) {
    if (!value) {
        return '';
    }
    return summaryMode === 'tripwise' ? String(value) : formatTwoDecimals(value);
}

/** Summary matrix: rows = vehicles, columns = period dates, values = qty or trips. */
function buildEquipmentSummaryReport(rows, fromDate, toDate, summaryMode) {
    const dateKeys = buildPeriodDateKeys(fromDate, toDate);
    const dateFields = dateKeys.map(summaryDateField);
    const vehicleMap = new Map();

    rows.forEach((row) => {
        const vehicle = row.vehicle_no || 'Unassigned';
        if (!vehicleMap.has(vehicle)) {
            const record = {
                id: `summary-${vehicle}`,
                vehicle_no: vehicle,
                total: 0,
            };
            dateFields.forEach((field) => {
                record[field] = 0;
            });
            vehicleMap.set(vehicle, record);
        }

        const record = vehicleMap.get(vehicle);
        const field = summaryDateField(row.delivery_challan_date);
        if (!Object.prototype.hasOwnProperty.call(record, field)) {
            return;
        }
        const increment = summaryMode === 'tripwise' ? 1 : Number(row.dc_qty || 0);
        record[field] += increment;
        record.total += increment;
    });

    const vehicleRecords = Array.from(vehicleMap.values())
        .sort((left, right) => compareText(left.vehicle_no, right.vehicle_no));

    const totals = {
        id: 'summary-total',
        vehicle_no: 'Total',
        is_total: true,
        total: 0,
    };
    dateFields.forEach((field) => {
        totals[field] = 0;
    });
    vehicleRecords.forEach((record) => {
        dateFields.forEach((field) => {
            totals[field] += Number(record[field] || 0);
        });
        totals.total += Number(record.total || 0);
    });

    const records = [
        {
            ...totals,
            ...Object.fromEntries(
                dateFields.map((field) => [field, formatSummaryValue(totals[field], summaryMode)])
            ),
            total: formatSummaryValue(totals.total, summaryMode),
        },
        ...vehicleRecords.map((record) => {
            const next = { ...record };
            dateFields.forEach((field) => {
                next[field] = formatSummaryValue(record[field], summaryMode);
            });
            next.total = formatSummaryValue(record.total, summaryMode);
            return next;
        }),
    ];

    const columns = [
        { fieldName: 'vehicle_no', headerName: 'Vehicle No', minSize: 120 },
        ...dateKeys.map((dateKey) => ({
            fieldName: summaryDateField(dateKey),
            headerName: dateKey,
            align: 'right',
            minSize: 92,
            headerStyle: { whiteSpace: 'nowrap' },
        })),
        {
            fieldName: 'total',
            headerName: 'Total',
            align: 'right',
            minSize: 88,
            getCellStyle: () => ({ fontWeight: 700, color: '#0f255f' }),
        },
    ];

    const chartRows = vehicleRecords.map((record) => ({
        label: record.vehicle_no,
        total: Number(record.total || 0),
    }));

    return { columns, records, chartRows };
}

function EquipmentReportTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);
    const [reportFormat, setReportFormat] = useState(() => {
        const saved = readSavedSettings();
        return saved.report_type === 'summary' ? 'qtywise' : 'all';
    });
    const [summaryColumns, setSummaryColumns] = useState([]);
    const [summaryChartRows, setSummaryChartRows] = useState([]);
    const [plants, setPlants] = React.useState([]);
    const [selectedPlant, setSelectedPlant] = useState([
        { value: 'select-all', label: 'Select All plants' },
    ]);
    const [selectedCustomer, setSelectedCustomer] = useState([
        { value: 'select-all', label: 'Select All customers' },
    ]);
    const [customers, setCustomers] = useState([]);
    const [selectedSite, setSelectedSite] = useState([
        { value: 'select-all', label: 'Select All sites' },
    ]);
    const [sites, setSites] = useState([]);
    const [selectedVehicleno, setSelectedVehicleno] = useState([
        { value: 'select-all', label: 'Select All vehicles' },
    ]);
    const [allEquipments, setAllEquipments] = useState([]);
    const [equipmentGroups, setEquipmentGroups] = useState([]);
    const [equipmentGroupId, setEquipmentGroupId] = useState(() => {
        const saved = readSavedSettings();
        return saved.equipment_group_id != null ? String(saved.equipment_group_id) : '';
    });
    const [reportSettings, setReportSettings] = useState(() => {
        const saved = readSavedSettings();
        const reportType = ['detailed', 'condensed', 'summary'].includes(saved.report_type)
            ? saved.report_type
            : 'detailed';
        return { report_type: reportType };
    });
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [settingsDraft, setSettingsDraft] = useState({
        selectedPlant: [],
        equipment_group_id: '',
        report_type: 'detailed',
    });
    const currentMonthStartDate = moment().startOf('month');
    const currentDate = moment();
    const [dates, setDates] = useState([currentMonthStartDate, currentDate]);

    const plantsWithSelectAll = useMemo(() => ([
        { value: 'select-all', label: 'Select All plants' },
        ...plants,
    ]), [plants]);

    const customersWithSelectAll = useMemo(() => ([
        { value: 'select-all', label: 'Select All customers' },
        ...customers,
    ]), [customers]);

    const sitesWithSelectAll = useMemo(() => ([
        { value: 'select-all', label: 'Select All sites' },
        ...sites,
    ]), [sites]);

    const vehicles = useMemo(() => {
        if (!equipmentGroupId) {
            return allEquipments.map(({ value, label }) => ({ value, label }));
        }
        return allEquipments
            .filter((item) => String(item.equip_type_id) === String(equipmentGroupId))
            .map(({ value, label }) => ({ value, label }));
    }, [allEquipments, equipmentGroupId]);

    const vehiclesWithSelectAll = useMemo(() => ([
        { value: 'select-all', label: 'Select All vehicles' },
        ...vehicles,
    ]), [vehicles]);

    React.useEffect(() => {
        getUserAllowedPlants(cookies)
            .then((plantList) => {
                const plantOptions = plantList.plant_list.filter((obj) => obj.status).map(
                    (plant) => ({ value: plant.id, label: plant.entity_name })
                );
                setPlants(plantOptions);

                const saved = readSavedSettings();
                const savedPlantIds = Array.isArray(saved.plant_ids) ? saved.plant_ids.map(String) : [];
                if (savedPlantIds.length) {
                    const restored = plantOptions.filter((plant) => savedPlantIds.includes(String(plant.value)));
                    if (restored.length === plantOptions.length) {
                        setSelectedPlant([{ value: 'select-all', label: 'Select All plants' }]);
                    } else if (restored.length) {
                        setSelectedPlant(restored);
                    } else {
                        setSelectedPlant([{ value: 'select-all', label: 'Select All plants' }]);
                    }
                } else {
                    setSelectedPlant([{ value: 'select-all', label: 'Select All plants' }]);
                }
            });
    }, []);

    React.useEffect(() => {
        getAllGroups(cookies)
            .then((response) => {
                const groups = getEquipmentTypeOptions(response.accounts_grouping_list || []);
                setEquipmentGroups(groups);

                const saved = readSavedSettings();
                const preferredId = saved.equipment_group_id != null
                    ? String(saved.equipment_group_id)
                    : '';
                const bySaved = preferredId
                    ? groups.find((group) => String(group.id) === preferredId)
                    : null;
                const byDefault = groups.find(
                    (group) => String(group.entity_name || '').trim().toUpperCase() === DEFAULT_EQUIPMENT_GROUP_NAME
                );
                const resolved = bySaved || byDefault || groups[0];
                if (resolved) {
                    setEquipmentGroupId(String(resolved.id));
                }
            });
    }, []);

    React.useEffect(() => {
        getAllCustomerMain(cookies)
            .then((customerList) => {
                const customerOptions = customerList.customer_list.filter((obj) => obj.status).map(
                    (customer) => ({ value: customer.id, label: customer.name })
                );
                setCustomers(customerOptions);
                setSelectedCustomer([{ value: 'select-all', label: 'Select All customers' }]);
            });
    }, []);

    React.useEffect(() => {
        getAllCustomerSites(cookies)
            .then((siteList) => {
                const siteOptions = mapConsigneeListToSiteOptions(
                    (siteList.consignee_list || []).filter((obj) => obj.status)
                );
                setSites(siteOptions);
                setSelectedSite([{ value: 'select-all', label: 'Select All sites' }]);
            })
            .catch(() => {
                setSites([]);
            });
    }, []);

    React.useEffect(() => {
        getAllEquipments(cookies)
            .then((equipmentObject) => {
                const equipments = (equipmentObject.equipment_list || [])
                    .filter((obj) => obj.status)
                    .map((equipment) => ({
                        value: equipment.id,
                        label: equipment.equip_name,
                        equip_type_id: equipment.equip_type?.id ?? equipment.equip_type_id ?? '',
                    }));
                setAllEquipments(equipments);
            });
    }, []);

    React.useEffect(() => {
        const selectAllActive = (selectedVehicleno || []).some((item) => item.value === 'select-all');
        if (selectAllActive || !selectedVehicleno?.length) {
            setSelectedVehicleno([{ value: 'select-all', label: 'Select All vehicles' }]);
            return;
        }
        const allowed = new Set(vehicles.map((item) => String(item.value)));
        const next = selectedVehicleno.filter((item) => allowed.has(String(item.value)));
        if (next.length !== selectedVehicleno.length) {
            setSelectedVehicleno(next.length ? next : [{ value: 'select-all', label: 'Select All vehicles' }]);
        }
    }, [vehicles]);

    const onCustomerSelect = (selected) => {
        HandleSelect(selected, selectedCustomer, setSelectedCustomer, customers, customersWithSelectAll);
    };

    const onSiteSelect = (selected) => {
        HandleSelect(selected, selectedSite, setSelectedSite, sites, sitesWithSelectAll);
    };

    const onVehiclenoSelect = (selected) => {
        HandleSelect(selected, selectedVehicleno, setSelectedVehicleno, vehicles, vehiclesWithSelectAll);
    };

    const openReportSettings = () => {
        setSettingsDraft({
            selectedPlant: selectedPlant || [],
            equipment_group_id: equipmentGroupId || '',
            report_type: reportSettings.report_type || 'detailed',
        });
        setShowSettingsModal(true);
    };

    const onSettingsPlantSelect = (selected) => {
        HandleSelect(
            selected,
            settingsDraft.selectedPlant,
            (value) => {
                setSettingsDraft((current) => ({ ...current, selectedPlant: value }));
            },
            plants,
            plantsWithSelectAll
        );
    };

    const applyReportSettings = () => {
        const draftPlants = settingsDraft.selectedPlant || [];
        const hasPlants = draftPlants.some((plant) => plant.value === 'select-all')
            || draftPlants.some((plant) => plant.value !== 'select-all');
        if (!hasPlants) {
            Swal.fire('Please select at least one plant in report settings.', '', 'info');
            return;
        }
        if (!settingsDraft.equipment_group_id) {
            Swal.fire('Please select an equipment group in report settings.', '', 'info');
            return;
        }

        const nextPlants = draftPlants.some((plant) => plant.value === 'select-all')
            ? plants
            : draftPlants.filter((plant) => plant.value !== 'select-all');
        const nextReportType = settingsDraft.report_type || 'detailed';
        setSelectedPlant(
            draftPlants.some((plant) => plant.value === 'select-all')
                || nextPlants.length === plants.length
                ? [{ value: 'select-all', label: 'Select All plants' }]
                : nextPlants
        );
        setEquipmentGroupId(String(settingsDraft.equipment_group_id));
        setReportSettings({ report_type: nextReportType });
        if (nextReportType === 'summary') {
            setReportFormat((current) => (
                ['qtywise', 'tripwise'].includes(current) ? current : 'qtywise'
            ));
            setData({ total_pages: 0, records: [] });
            setSummaryColumns([]);
            setSummaryChartRows([]);
        } else if (['qtywise', 'tripwise'].includes(reportFormat)) {
            setReportFormat('all');
            setSummaryColumns([]);
            setSummaryChartRows([]);
        }
        localStorage.setItem(EQUIPMENT_REPORT_SETTINGS_KEY, JSON.stringify({
            plant_ids: nextPlants.map((plant) => plant.value),
            equipment_group_id: settingsDraft.equipment_group_id,
            report_type: nextReportType,
        }));
        setShowSettingsModal(false);
    };

    const isSelectAllActive = (selected) => (
        Array.isArray(selected) && selected.some((item) => item.value === 'select-all')
    );

    const areAllOptionsSelected = (options, selected) => {
        if (!options?.length) {
            return false;
        }
        if (isSelectAllActive(selected)) {
            return true;
        }
        if (!selected?.length) {
            return false;
        }
        return options.every((option) =>
            selected.some((item) => String(item.value) === String(option.value))
        );
    };

    const joinSelectedIds = (selected, options) => {
        if (areAllOptionsSelected(options, selected)) {
            return '*';
        }
        return (selected || [])
            .filter((item) => item.value !== 'select-all')
            .map((item) => item.value)
            .join(',');
    };

    const getSelectedPlantIds = () => {
        if (isSelectAllActive(selectedPlant) || areAllOptionsSelected(plants, selectedPlant)) {
            return '*';
        }
        return (selectedPlant || [])
            .filter((item) => item.value !== 'select-all')
            .map((item) => item.value)
            .join(',');
    };

    const isSummaryReport = reportSettings.report_type === 'summary';

    const constructFormData = () => {
        const apiReportFormat = isSummaryReport
            ? 'all'
            : (reportFormat === 'datewise'
                ? 'datewise'
                : (reportFormat === 'customerwise' ? 'customerwise' : 'all'));
        // Fetch detailed rows; condense/summary aggregation is done on the client.
        return {
            plant_ids: getSelectedPlantIds() || '*',
            customer_ids: joinSelectedIds(selectedCustomer, customers) || '*',
            consignee_ids: joinSelectedIds(selectedSite, sites) || '*',
            sales_rep_ids: '*',
            vehicle_ids: joinSelectedIds(selectedVehicleno, vehicles) || '*',
            product_ids: '*',
            from_date: dates[0].format('DD-MM-YYYY'),
            upto_date: dates[1].format('DD-MM-YYYY'),
            report_format: apiReportFormat,
            report_type: 'detailed',
        };
    };

    const columns = useMemo(() => {
        if (isSummaryReport) {
            return summaryColumns.length
                ? summaryColumns
                : [{ fieldName: 'vehicle_no', headerName: 'Vehicle No' }];
        }
        if (reportSettings.report_type === 'condensed') {
            return EQUIPMENT_CONDENSED_COLUMNS;
        }
        return EQUIPMENT_REPORT_COLUMNS;
    }, [isSummaryReport, summaryColumns, reportSettings.report_type]);

    const handleGenerate = () => {
        const plantIds = getSelectedPlantIds();
        if (!plantIds) {
            Swal.fire('Please select at least one plant in report settings.', '', 'info');
            return;
        }
        if (!reportFormat) {
            Swal.fire('Please select a report format.', '', 'info');
            return;
        }
        if (isSummaryReport && !['qtywise', 'tripwise'].includes(reportFormat)) {
            Swal.fire('Please select Qtywise or Tripwise for Summary.', '', 'info');
            return;
        }
        const formData = constructFormData();
        setIsLoading(true);
        getDispatchReport(cookies, formData)
            .then((response) => {
                let rows = (response.dispatch_report_list || []).map(mapEquipmentReportRow);
                if (isSummaryReport) {
                    const summary = buildEquipmentSummaryReport(
                        rows,
                        dates[0],
                        dates[1],
                        reportFormat,
                    );
                    setSummaryColumns(summary.columns);
                    setSummaryChartRows(summary.chartRows || []);
                    setData({
                        total_pages: 0,
                        records: summary.records,
                    });
                    setIsLoading(false);
                    return;
                }
                setSummaryColumns([]);
                setSummaryChartRows([]);
                if (reportSettings.report_type === 'condensed') {
                    rows = aggregateEquipmentCondensedRows(rows);
                }
                rows = buildEquipmentGroupedRows(rows, reportFormat);
                setData({
                    total_pages: 0,
                    records: rows,
                });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error.response?.data || getApiErrorMessage(error));
                setData({ total_pages: 0, records: [] });
                setSummaryColumns([]);
                setSummaryChartRows([]);
                setIsLoading(false);
            });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        handleGenerate();
    };

    return (
        <div className="report-embedded-page">
            <form className="form" onSubmit={handleSubmit}>
                <div className="container">
                    <Row>
                        <Col xl={12} lg={12} md={12}>
                            <div className="report-legacy-filters form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <div className="report-legacy-filters-fields">
                                    <ReactSelect
                                        options={customersWithSelectAll}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable
                                        value={selectedCustomer}
                                        onChange={onCustomerSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please select Customer"
                                    />
                                    <ReactSelect
                                        options={sitesWithSelectAll}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable
                                        value={selectedSite}
                                        onChange={onSiteSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select Site"
                                    />
                                    <ReactSelect
                                        options={vehiclesWithSelectAll}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable
                                        value={selectedVehicleno}
                                        onChange={onVehiclenoSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select Vehicle"
                                    />
                                </div>
                                <div className="report-legacy-filters-row">
                                    <RangePicker
                                        value={dates}
                                        format="DD-MM-YYYY"
                                        onChange={(values) => {
                                            if (values && values.length === 2) {
                                                setDates(values);
                                            }
                                        }}
                                        style={{ width: '270px' }}
                                    />
                                    <select
                                        className="browser-default custom-select col-sm-3"
                                        style={{ width: '200px' }}
                                        value={reportFormat}
                                        onChange={(event) => setReportFormat(event.target.value)}
                                    >
                                        {isSummaryReport ? (
                                            <>
                                                <option value="qtywise">Qtywise</option>
                                                <option value="tripwise">Tripwise</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value="all">All</option>
                                                <option value="datewise">Datewise</option>
                                                <option value="customerwise">Customerwise</option>
                                                <option value="vehiclewise">Vehiclewise</option>
                                                <option value="driverwise">Driverwise</option>
                                            </>
                                        )}
                                    </select>
                                    <div className="report-legacy-actions">
                                        <SettingsIcon onClickHandler={openReportSettings} variant="inline" />
                                        <Button
                                            color="primary"
                                            className="list-page-toolbar-generate-btn"
                                            type="submit"
                                            disabled={isLoading}
                                        >
                                            Generate
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </form>
            <Modal isOpen={showSettingsModal} toggle={() => setShowSettingsModal(false)} centered>
                <ModalHeader toggle={() => setShowSettingsModal(false)}>Report Settings</ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <label className="font-weight-bold d-block mb-2">Plant</label>
                        <ReactSelect
                            options={plantsWithSelectAll}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{ Option, ValueContainer }}
                            isClearable
                            value={settingsDraft.selectedPlant}
                            onChange={onSettingsPlantSelect}
                            className="react-select-dropdown"
                            placeholder="Please select Plant"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="font-weight-bold d-block mb-2">Equipment Group</label>
                        <select
                            className="browser-default custom-select"
                            value={settingsDraft.equipment_group_id || ''}
                            onChange={(event) => setSettingsDraft((current) => ({
                                ...current,
                                equipment_group_id: event.target.value,
                            }))}
                        >
                            <option value="">Please select Equipment Group</option>
                            {equipmentGroups.map((group) => (
                                <option key={group.id} value={group.id}>
                                    {formatEquipGroupLabel(group.entity_name)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="font-weight-bold d-block mb-2">Report Type</label>
                        <select
                            className="browser-default custom-select"
                            value={settingsDraft.report_type || 'detailed'}
                            onChange={(event) => setSettingsDraft((current) => ({
                                ...current,
                                report_type: event.target.value,
                            }))}
                        >
                            <option value="detailed">Detailed</option>
                            <option value="condensed">Condensed</option>
                            <option value="summary">Summary</option>
                        </select>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
                    <Button color="primary" onClick={applyReportSettings}>Apply</Button>
                </ModalFooter>
            </Modal>
            <div className="container">
                {data.records.length > 0 ? (
                    <div className="list-page-summary-bar">
                        <div className="list-page-summary-primary">
                            <span className="list-page-summary-label">
                                {isSummaryReport
                                    ? `EQUIPMENT SUMMARY — ${reportFormat === 'tripwise' ? 'TRIPWISE' : 'QTYWISE'}`
                                    : 'FILTERED VIEW'}
                            </span>
                            <span className="list-page-summary-count">
                                {isSummaryReport
                                    ? `${dates[0].format('DD-MM-YYYY')} to ${dates[1].format('DD-MM-YYYY')}`
                                    : (
                                        <>
                                            {data.records.filter((row) => !row.is_group_header && !row.is_total).length}{' '}
                                            {data.records.filter((row) => !row.is_group_header && !row.is_total).length === 1 ? 'record' : 'records'}
                                        </>
                                    )}
                            </span>
                            {isSummaryReport ? (
                                <span className="list-page-summary-label" style={{ marginTop: 4 }}>
                                    {data.records.filter((row) => !row.is_group_header && !row.is_total).length}{' '}
                                    {data.records.filter((row) => !row.is_group_header && !row.is_total).length === 1 ? 'record' : 'records'}
                                </span>
                            ) : null}
                        </div>
                    </div>
                ) : null}
                {isSummaryReport && summaryChartRows.length > 0 ? (
                    <DispatchSummaryChart
                        rows={summaryChartRows}
                        title={`Equipment Summary — ${reportFormat === 'tripwise' ? 'Tripwise' : 'Qtywise'} by Vehicle`}
                        period={`${dates[0].format('DD-MM-YYYY')} to ${dates[1].format('DD-MM-YYYY')}`}
                        valueLabel={reportFormat === 'tripwise' ? 'Trips' : 'Qty'}
                    />
                ) : null}
                <div className="list-page-table-wrap">
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title={
                                    isSummaryReport
                                        ? `Equipment Report - Summary (${reportFormat === 'tripwise' ? 'Tripwise' : 'Qtywise'})`
                                        : 'Equipment Report'
                                }
                                columns={columns}
                                data={data.records}
                                exportFilename="equipment_report"
                                isLoading={isLoading}
                                left_pinned={isSummaryReport ? ['vehicle_no'] : undefined}
                                compactColumns={isSummaryReport}
                                enableSorting={!isSummaryReport}
                                getRowSx={(row) => (
                                    (row.is_group_header || row.is_total)
                                        ? {
                                            backgroundColor: '#e8eef9 !important',
                                            '&:hover': { backgroundColor: '#d7e3f7 !important' },
                                            '& td': {
                                                borderTop: row.is_total ? '2px solid #1a3f8f' : '2px solid #1a3f8f',
                                                borderBottom: row.is_total ? '2px solid #1a3f8f' : undefined,
                                                fontWeight: 700,
                                                color: '#0f255f',
                                            },
                                        }
                                        : undefined
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

}

export default EquipmentReportTable;